import { getGlobalI18nInstance, type TranslationKeysType } from '@/lib/i18n'
import en from '@/locales/en'
import fr from '@/locales/fr'

/**
 * Titre du document (WCAG 2.4.2 — « Titre de page »).
 *
 * Le critère demande un titre qui DÉCRIT le sujet de la page. L'application
 * posait auparavant le seul nom de l'organisation, et seulement une fois la
 * requête white-label résolue : le document restait sans titre pendant tout le
 * démarrage, et toutes les pages partageaient le même intitulé.
 *
 * Le titre est ici recomposé à chaque navigation sous la forme
 * « Sujet de la page – Organisation ». La marque reste en second : les
 * lecteurs d'écran annoncent le début du titre en premier, c'est donc le sujet
 * qui doit s'y trouver.
 *
 * L'état vit sur `globalThis` : un HMR Vite peut charger ce module deux fois
 * (router vs layout) et l'un écraserait l'onglet avec le seul nom de marque.
 */

/**
 * Repli utilisé tant que le white-label n'a pas répondu, et sur les instances
 * dont le setting TITLE est vide. Volontairement générique : le produit est
 * white-labellisé, aucune marque ne peut être codée en dur.
 */
export const FALLBACK_APP_TITLE = 'Plateforme de mentorat'

type TitleState = {
  appTitle: string
  routeTitleKey: TranslationKeysType | ''
}

const TITLE_STATE_KEY = '__constelDocumentTitle'

function state(): TitleState {
  const store = globalThis as typeof globalThis & { [TITLE_STATE_KEY]?: TitleState }
  if (!store[TITLE_STATE_KEY]) {
    store[TITLE_STATE_KEY] = { appTitle: '', routeTitleKey: '' }
  }
  return store[TITLE_STATE_KEY]
}

function translate(key: TranslationKeysType): string {
  const i18n = getGlobalI18nInstance()?.global as unknown as { t: (key: string) => string } | undefined
  const fromI18n = i18n?.t(key)
  if (fromI18n && fromI18n !== key) return fromI18n

  const locale = document.documentElement.getAttribute('lang') || 'fr'
  const messages = locale.startsWith('en') ? en : fr
  return messages[key] || fr[key] || ''
}

function apply() {
  const { appTitle, routeTitleKey } = state()
  const brand = appTitle || FALLBACK_APP_TITLE
  const pageTitle = routeTitleKey ? translate(routeTitleKey) : ''

  document.title = pageTitle ? `${pageTitle} – ${brand}` : brand
}

/** Nom de l'organisation (setting white-label TITLE). */
export function setAppTitle(title: string | undefined) {
  state().appTitle = title ?? ''
  apply()
}

/** Sujet de la page courante, sous forme de clé i18n (`meta.titleKey` de la route). */
export function setRouteTitleKey(key: TranslationKeysType | undefined) {
  state().routeTitleKey = key ?? ''
  apply()
}
